import { Box, Button, Typography } from '@mui/material';
import { t } from 'i18next';

import { SubmissionsTable } from '@views/pages/DataAnalysis/components/Submissions/components';
import { useSubmissions } from '@views/pages/DataAnalysis/components/Submissions/hooks';
import { theme } from '@store';
import { Select } from '@ui';
import { Option } from '@common/types';
import { FC } from 'react';

interface SubmissionsProps {
  programId: string;
  orgId: string;
  setOrgId: (orgId: string) => void;
  setProgramId: (programId: string) => void;
  organizationOptions: Option[];
  programOptions: Option[];
}

export const Submissions: FC<SubmissionsProps> = ({
  programId,
  orgId,
  setOrgId,
  setProgramId,
  organizationOptions,
  programOptions,
}) => {
  const {
    entries,
    setFilters,
    filters,
    totalCount,
    isLoading,
    patients_ready_to_submit,
    handleChangeOrgId,
    handleChangeProgramId,
    handleGenerateSubmissions,
    isGeneratePending,
  } = useSubmissions({
    programId,
    orgId,
    setOrgId,
    setProgramId,
  });

  const columns = [
    {
      id: 'download_date',
      label: t('submissions.table.headers.download_date'),
      sortable: true,
      width: 150,
    },
    {
      id: 'organization_name',
      label: t('submissions.table.headers.organization_name'),
      sortable: false,
      width: 180,
    },
    {
      id: 'program_name',
      label: t('submissions.table.headers.program_name'),
      sortable: false,
      width: 180,
    },
    {
      id: 'date',
      label: t('submissions.table.headers.date'),
      sortable: true,
      width: 120,
    },
    {
      id: 'patients',
      label: t('submissions.table.headers.patients'),
      sortable: true,
      width: 130,
    },
    {
      id: 'pending_patients',
      label: t('submissions.table.headers.pending_patients'),
      sortable: true,
      width: 120,
    },
    {
      id: 'aligned_patients',
      label: t('submissions.table.headers.aligned_patients'),
      sortable: true,
      width: 110,
    },
    {
      id: 'rejected_patients',
      label: t('submissions.table.headers.rejected_patients'),
      sortable: true,
      width: 90,
    },
    {
      id: 'action',
      label: '',
      sortable: false,
      width: 50,
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'end',
          padding: '24px',
          backgroundColor: theme.palette.common.white,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          mb: '32px',
        }}
      >
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 2 }}>
            {t('submissions.patientsReadyToSubmit')}
          </Typography>
          <Typography variant='h3'>{patients_ready_to_submit}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Select
            sx={{ width: '256px' }}
            name='submission-organization'
            size='medium'
            label={t('submissions.filters.labels.organization')}
            value={orgId}
            onChange={handleChangeOrgId}
            options={organizationOptions}
          />
          <Select
            sx={{ width: '256px' }}
            name='submission-program'
            size='medium'
            label={t('submissions.filters.labels.program')}
            value={programId}
            onChange={handleChangeProgramId}
            options={programOptions}
          />
          <Button
            sx={{
              fontWeight: 'regular',
              fontSize: '1rem',
              color: 'white',
              height: 42,
              padding: '8px 22px',
            }}
            color='secondary'
            variant='contained'
            disabled={isGeneratePending || patients_ready_to_submit === 0}
            onClick={handleGenerateSubmissions}
          >
            {t('submissions.generateBtn')}
          </Button>
        </Box>
      </Box>

      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        {t('submissions.allSubmissions', { value: totalCount })}
      </Typography>

      <SubmissionsTable
        rows={entries}
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        totalCount={totalCount}
        loading={isLoading}
      />
    </>
  );
};
